import type { DecisionResponse } from "./types.js";

const ACTIONS = new Set(["approve", "deny", "defer", "cancel"]);

/**
 * Lift the owner's answer to a decision out of a decrypted envelope.
 *
 * The app answers a `decision_request` with an envelope carrying
 * `decision: { decision_id, selected_option_id }` (or `action: "defer" |
 * "cancel"` instead of an option). Every receive path reduces the envelope to
 * its `text` before emitting, so without this the answer never reached
 * `waitForDecision()`.
 *
 * Returns undefined for anything that is not an answer — in particular a
 * decision REQUEST (it carries `options`), so an echoed request can never be
 * mistaken for the owner's reply.
 */
export function decisionReplyFromEnvelope(parsed: unknown): DecisionResponse | undefined {
  if (parsed === null || typeof parsed !== "object") return undefined;
  const decision = (parsed as { decision?: unknown }).decision;
  if (decision === null || typeof decision !== "object") return undefined;
  const d = decision as Record<string, unknown>;
  if (typeof d.decision_id !== "string" || d.decision_id.length === 0) return undefined;
  if (Array.isArray(d.options)) return undefined;

  const selected = typeof d.selected_option_id === "string" ? d.selected_option_id : undefined;
  const action = typeof d.action === "string" && ACTIONS.has(d.action)
    ? (d.action as DecisionResponse["action"])
    : undefined;
  if (selected === undefined && action === undefined) return undefined;

  const reply: DecisionResponse = { decision_id: d.decision_id };
  if (selected !== undefined) reply.selected_option_id = selected;
  if (action !== undefined) reply.action = action;
  if (typeof d.resolved_at === "string") reply.resolved_at = d.resolved_at;
  if (typeof d.defer_until === "string") reply.defer_until = d.defer_until;
  if (typeof d.defer_reason === "string") reply.defer_reason = d.defer_reason;
  if (typeof d.reason === "string") reply.reason = d.reason;
  return reply;
}
